import React, { useMemo } from "react";
import {
    Box,
    Typography,
    Switch,
    Stack,
    Grid,
    FormHelperText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import StepContainer from "../../components/StepContainer";
import { CommonLandingPageSteps } from "../../AddEditLandingPage.types";

import { ReactComponent as BasicTemplate } from "../../assets/basic.svg";
import { ReactComponent as CoverImage } from "../../assets/coverImage.svg";
import { ReactComponent as CustomTemplate } from "../../assets/custom.svg";
import { ReactComponent as GamificationTemplate } from "../../assets/gamification.svg";
import { ReactComponent as LeadTemplate } from "../../assets/lead.svg";
import { ReactComponent as FunnelTemplate } from "../../assets/funnel-light.svg";

import { ReactComponent as BasicTemplateActive } from "../../assets/basicActive.svg";
import { ReactComponent as CoverImageActive } from "../../assets/coverImageActive.svg";
import { ReactComponent as CustomTemplateActive } from "../../assets/customActive.svg";
import { ReactComponent as GamificationTemplateActive } from "../../assets/gamificationActive.svg";
import { ReactComponent as LeadTemplateActive } from "../../assets/leadActive.svg";
import { ReactComponent as FunnelTemplateActive } from "../../assets/funnel-dark.svg"

import useUpdateEffect from "hooks/useUpdateEffect";

// 🧩 Layout Options
const layoutOptions = [
    {
        value: "basic",
        label: "Basic",
        description: "Clean and fast-loading layout.",
        icon: BasicTemplate,
        activeIcon: BasicTemplateActive,
    },
    {
        value: "cover",
        label: "Cover Image",
        description: "Add a striking visual.",
        icon: CoverImage,
        activeIcon: CoverImageActive,
    },
    {
        value: "video",
        label: "Cover Video",
        description: "Introduce your offer with a short video.",
        icon: CoverImage,
        activeIcon: CoverImageActive,
    },
    {
        value: "lead",
        label: "Lead Form",
        description: "Introduce your offer with a short video.",
        icon: LeadTemplate,
        activeIcon: LeadTemplateActive,
    },
    {
        value: "custom",
        label: "Custom",
        description: "Upload your own custom layout.",
        icon: CustomTemplate,
        activeIcon: CustomTemplateActive,
    },
    {
        value: "gamification",
        label: "Gamification",
        description: "Simple game mechanic to boost interaction.",
        icon: GamificationTemplate,
        activeIcon: GamificationTemplateActive,
    },
    {
        value: "funnel",
        label: "Funnel",
        description:
            "Create a funnel to guide your users through a series of steps.",
        icon: FunnelTemplate,
        activeIcon: FunnelTemplateActive,
    },
];

// ✅ Yup validation schema
const schema = yup.object().shape({
    template_id: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Please select a layout."),
    theme: yup.string().oneOf(["light", "dark"]).required(),
});

export default function TemplateStep({
    onSave,
    form,
    templates,
    customSources,
    landingPageDetails,
    gamificationTemplates,
    setIsStepDirty,
}) {
    const {
        control,
        handleSubmit,
        formState: { isDirty, errors },
        watch,
    } = useForm({
        defaultValues: {
            template_id: "",
            theme: "light",
            ...form,
        },
        resolver: yupResolver(schema),
    });

    const onSubmit = (data) => {
        onSave({ stepName: CommonLandingPageSteps.TEMPLATE, data });
    };

    const templateID = watch("template_id");
    const selectedTemplate = useMemo(
        () => templates?.data?.find((template) => template?.id === templateID),
        [templateID],
    );
    useUpdateEffect(() => {
        setIsStepDirty(isDirty);
    }, [isDirty]);
    // Filter out templates that don't have corresponding layout options
    const availableTemplates = useMemo(() => {
        if (!templates?.data) return [];
        return templates.data.filter((template) => {
            const hasLayoutOption = layoutOptions.find(
                (option) => option.value === template.name,
            );
            return hasLayoutOption;
        });
    }, [templates?.data]);
    return (
        <StepContainer
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isDirty={isDirty}
        >
            <Box sx={{ mb: { xs: 3, md: 2 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "24px", sm: "28px", md: "32px" },
                        mb: { xs: 1, md: 2 },
                        textAlign: { xs: "center", md: "left" },
                    }}
                >
                    Select Layout
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        textAlign: { xs: "center", md: "left" },
                        lineHeight: 1.5,
                    }}
                >
                    Choose the perfect layout for your landing page
                </Typography>
            </Box>
            <Controller
                name="template_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Box>
                        <Grid
                            container
                            rowGap={4}
                            sx={{
                                ...(error && {
                                    border: "1px solid",
                                    borderColor: "error.main",
                                    borderRadius: 2,
                                    p: 2,
                                }),
                            }}
                        >
                            {availableTemplates?.map((option) => {
                                const templateInfo = layoutOptions?.find(
                                    (template) =>
                                        template?.value === option?.name,
                                );
                                const isSelected = field.value === option.id;
                                const Icon = isSelected
                                    ? templateInfo.activeIcon
                                    : templateInfo.icon;
                                return (
                                    <Grid
                                        key={templateInfo.value}
                                        onClick={() =>
                                            field.onChange(option.id)
                                        }
                                        sx={{
                                            cursor: "pointer",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: {
                                                xs: "center",
                                                md: "flex-start",
                                            },
                                            justifyContent: "center",
                                        }}
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <Box height={"152px"}>
                                            <Icon />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            className="mt-3"
                                        >
                                            {templateInfo.label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{
                                                pr: 4,
                                            }}
                                        >
                                            {templateInfo.description}
                                        </Typography>
                                    </Grid>
                                );
                            })}
                        </Grid>
                        {error && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {error.message}
                            </FormHelperText>
                        )}
                    </Box>
                )}
            />

            {
                <Box
                    sx={{
                        opacity: selectedTemplate?.fields?.find(
                            (field) => field?.name === "theme",
                        )
                            ? 1
                            : 0,
                    }}
                >
                    {/* Theme Toggle */}
                    <Typography fontWeight={600} mb={1}>
                        Page Theme
                    </Typography>
                    <Typography variant="body2" mb={1}>
                        Choose the theme that best matches your brand’s tone.
                    </Typography>
                    <Controller
                        name="theme"
                        control={control}
                        render={({ field }) => (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                mb={4}
                            >
                                <Typography>Dark</Typography>
                                <Switch
                                    checked={field.value === "light"}
                                    onChange={() =>
                                        field.onChange(
                                            field.value === "light"
                                                ? "dark"
                                                : "light",
                                        )
                                    }
                                />
                                <Typography>Light</Typography>
                            </Stack>
                        )}
                    />
                </Box>
            }
        </StepContainer>
    );
}
